import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Subscribe } from 'unstated';
import SmallModalLayout from '../structure/SmallModalLayout';
import ModalHeader from '../general/ModalHeader';
import TextField from '../forms/TextField';
import Switch from '../forms/Switch';
import Button from '../general/Button';
import OrganizationAvatarUpload from '../general/OrganizationAvatarUpload';
import { css } from '@emotion/css';
import { AIAnsweringFeatureFlagId, FoodOrderingFeatureFlagId } from '../../constants/featureFlagIds';
import SparkContainer from '../../hooks/SparkContainer';
import OrgSetupContainer from '../../hooks/OrgSetupContainer';
import OrganizationContainer from '../../state/containers/OrganizationContainer';
import MediaContainer from '../../state/containers/MediaContainer';
import OrganizationModel from '../../models/organizations/api/OrganizationModel';

interface CreateOrganizationModalProps {
    isVisible: boolean;
    onClose: () => void;
    onOrganizationCreated: (org: OrganizationModel) => void;
}

const CreateOrganizationModal: React.FC<CreateOrganizationModalProps> = (props) => {
    const [name, setName] = useState('');
    const [nameError, setNameError] = useState('');
    const [isMultiLingual, setIsMultiLingual] = useState(false);
    const [createdOrg, setCreatedOrg] = useState<OrganizationModel | null>(null);
    const [avatarErrors, setAvatarErrors] = useState<string[]>([]);

    const orgSetupContainer = OrgSetupContainer.useContainer();
    const sparkContainer = SparkContainer.useContainer();

    const handleClose = () => {
        setName('');
        setNameError('');
        setIsMultiLingual(false);
        setCreatedOrg(null);
        setAvatarErrors([]);
        props.onClose();
    };

    const validateForm = (): boolean => {
        if (!name || name.trim().length < 2) {
            setNameError('You must enter a name for your organization with at least 2 characters');
            return false;
        }
        setNameError('');
        return true;
    };

    const handleSubmit = async (
        e: React.FormEvent,
        orgContainer: OrganizationContainer
    ) => {
        e.preventDefault();
        if (!validateForm()) return;

        const featureFlagId = sparkContainer.inSpark
            ? AIAnsweringFeatureFlagId
            : FoodOrderingFeatureFlagId;

        const setupOrgResponse = await orgSetupContainer.setupOrg({
            organizationName: name.trim(),
            featureFlagId
        });

        if (setupOrgResponse.resultType === 'Ok') {
            const newOrg: OrganizationModel = {
                id: setupOrgResponse.data.id,
                secret: setupOrgResponse.data.secret,
                name: setupOrgResponse.data.name,
                description: setupOrgResponse.data.description,
                instructionsMarkdown: setupOrgResponse.data.instructionsMarkdown ?? ''
            };

            // Set as current org so avatar upload and updateOrganizationInfo work
            await orgContainer.setCurrentOrgById(newOrg.id);

            // Save isMultiLingual
            if (isMultiLingual) {
                await orgContainer.updateOrganizationInfo({
                    name: newOrg.name,
                    description: newOrg.description || '',
                    imageUri: '',
                    isMultiLingual: true
                });
            }

            setCreatedOrg(newOrg);
            props.onOrganizationCreated(newOrg);
        }
    };

    const handleDone = () => {
        handleClose();
    };

    return (
        <Subscribe to={[OrganizationContainer, MediaContainer]}>
            {(orgContainer: OrganizationContainer, mediaContainer: MediaContainer) => (
                <SmallModalLayout
                    isVisible={props.isVisible}
                    onClose={handleClose}
                    innerClassName={modalInnerStyle}
                >
                    <ModalHeader title="Create a New Organization" onClose={handleClose} />
                    <FormContainer onSubmit={(e) => handleSubmit(e, orgContainer)}>
                        <TextField
                            name="name"
                            useForm={false}
                            disabled={orgSetupContainer.isLoading || !!createdOrg}
                            required
                            value={name}
                            label="Organization Name"
                            labelStyle={labelStyle}
                            customRequiredLabelStyle={customRequiredLabelStyle}
                            placeholder="What's the name of your organization?"
                            onChange={(e) => {
                                setName(e.target.value);
                                if (nameError) setNameError('');
                            }}
                        />
                        {nameError && <ErrorMessage>{nameError}</ErrorMessage>}
                        <OrganizationAvatarUpload
                            organizationId={createdOrg?.id}
                            organizationName={createdOrg?.name ?? name}
                            currentImageUri={createdOrg ? orgContainer.state.currentOrganization?.imageUri : null}
                            orgContainer={orgContainer}
                            mediaContainer={mediaContainer}
                            size="small"
                            showLabel={true}
                            showHint={!!createdOrg && avatarErrors.length === 0}
                            disabled={!createdOrg}
                            isLoading={orgSetupContainer.isLoading || orgContainer.state.isLoadingOrgs}
                            onError={setAvatarErrors}
                            onClearError={() => setAvatarErrors([])}
                        />
                        {avatarErrors.length > 0 && (
                            <ErrorMessage>{avatarErrors.join(' ')}</ErrorMessage>
                        )}
                        <SwitchSection>
                            <Switch
                                name="isMultiLingual"
                                checked={isMultiLingual}
                                onChange={(checked) => setIsMultiLingual(checked)}
                                label="Supports Multiple Languages"
                                labelStyle={switchLabelStyle}
                                disabled={orgSetupContainer.isLoading}
                            />
                        </SwitchSection>
                        <ButtonContainer>
                            <Button
                                themes={['white']}
                                text="Cancel"
                                type="button"
                                onClick={handleClose}
                                disabled={orgSetupContainer.isLoading}
                            />
                            {createdOrg ? (
                                <Button
                                    themes={['primary']}
                                    text="Done"
                                    type="button"
                                    onClick={handleDone}
                                />
                            ) : (
                                <Button
                                    loading={orgSetupContainer.isLoading}
                                    disabled={orgSetupContainer.isLoading || !name.trim()}
                                    themes={['primary']}
                                    text="Create"
                                    type="submit"
                                />
                            )}
                        </ButtonContainer>
                    </FormContainer>
                </SmallModalLayout>
            )}
        </Subscribe>
    );
};

const modalInnerStyle = css`
    display: flex;
    flex-direction: column;
`;

const FormContainer = styled.form`
    padding: 24px 32px 32px 32px;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow-y: auto;
`;

const SwitchSection = styled.div`
    margin: 16px 16px 24px 0px;
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    margin-top: auto;
    padding-top: 24px;
`;

const ErrorMessage = styled.div`
    color: red;
    font-size: 13px;
    margin-top: -16px;
    margin-bottom: 8px;
    font-family: Muli;
`;

const labelStyle = css`
    font-size: 14px;
    font-weight: 600;
`;

const customRequiredLabelStyle = css`
    font-size: 14px;
    color: red;
    margin-left: 2px;
    font-weight: 600;
`;

const switchLabelStyle = css`
    font-size: 14px;
    font-weight: 600;
`;

export default CreateOrganizationModal;
